import { classes, loadDeferredImages } from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";
import { Swiper } from "@theme/swiper";

const TAB_FADE_MS = 300;

class ProductFeedSlider extends HTMLCustomElement {
  /** @type {WeakMap<Element, import("swiper").Swiper>} */
  #swiperByEl = new WeakMap();

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.refs.button.all) {
      for (const button of this.refs.button.all) {
        this.refs.buttons.appendChild(button);

        const tabForButton = () =>
          this.refs.tab.all.find(
            (tab) => tab.dataset.blockId === button.dataset.blockId,
          );

        for (const type of ["pointerenter", "focus"]) {
          button.addEventListener(
            type,
            () => loadDeferredImages(tabForButton()),
            { once: true },
          );
        }

        button.addEventListener("click", () => {
          const nextTab = tabForButton();
          const prevActive = this.refs.tab.all.find((tab) =>
            tab.classList.contains(classes.active),
          );

          if (!nextTab || nextTab === prevActive) return;

          this.#activateTab(nextTab, button);
        });
      }
    }

    if (this.refs.tab.all) {
      const firstTab = this.refs.tab.all[0];
      const firstButton = this.refs.button.all[0];
      if (firstTab) this.#activateTab(firstTab, firstButton);
    }

    if (this.refs.swiper.all) {
      for (const swiperEl of this.refs.swiper.all) {
        swiperEl.setAttribute("data-lenis-prevent-horizontal", "");
        const instance = new Swiper(swiperEl, {
          slidesPerView: 1.5,
          spaceBetween: 16,
          freeMode: true,
          centerInsufficientSlides: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          on: {
            sliderFirstMove: (swiper) => swiper.el.classList.add("is-dragging"),
            touchEnd: (swiper) => swiper.el.classList.remove("is-dragging"),
          },
          breakpoints: {
            768: {
              slidesPerView: 2.5,
              spaceBetween: 16,
            },
            1024: {
              slidesPerView: 4.5,
            },
          },
        });
        this.#swiperByEl.set(swiperEl, instance);
      }

      const initialTab = this.refs.tab.all?.find((tab) =>
        tab.classList.contains(classes.active),
      );
      if (initialTab) this.#refreshSwiperForTab(initialTab);
    }
  }

  /**
   * @param {Element} nextTab
   * @param {Element} [button]
   */
  #activateTab(nextTab, button) {
    this.refs.tab.all.forEach((tab) => {
      tab.classList.toggle(classes.active, tab === nextTab);
    });

    if (this.refs.button?.all) {
      this.refs.button.all.forEach((btn) => {
        btn.classList.toggle(classes.active, btn === button);
      });
    }

    loadDeferredImages(nextTab);
    this.#syncCollectionCta(nextTab);
    this.#refreshSwiperForTab(nextTab);
  }

  /**
   * Shop all follows the active collection tab; otherwise keeps the editor URL.
   * @param {Element | null | undefined} tab
   */
  #syncCollectionCta(tab) {
    const url =
      tab?.getAttribute("data-collection-url") ||
      tab?.querySelector("[data-collection-url]")?.getAttribute(
        "data-collection-url",
      ) ||
      "";

    for (const cta of this.querySelectorAll(".block--button")) {
      if (!(cta instanceof HTMLAnchorElement)) continue;

      if (!cta.dataset.defaultHref) {
        cta.dataset.defaultHref = cta.getAttribute("href") ?? "";
      }

      cta.href = url || cta.dataset.defaultHref;
    }
  }

  /**
   * Swiper measures while hidden (opacity 0); refresh after the tab fade so widths are correct.
   * @param {Element} tab
   */
  #refreshSwiperForTab(tab) {
    const swiperEl = tab.querySelector(".swiper");
    const swiper = swiperEl ? this.#swiperByEl.get(swiperEl) : undefined;
    if (!swiper) return;

    requestAnimationFrame(() => {
      swiper.update();
    });

    window.setTimeout(() => {
      swiper.update();
    }, TAB_FADE_MS);
  }
}

if (!customElements.get("product-feed-slider"))
  customElements.define("product-feed-slider", ProductFeedSlider);
